import React, { useContext, useEffect, useRef, useCallback } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import { useSelector } from 'react-redux';

import { BodyMediumText } from '@prisjakt/ui/text/variants';
import { Row } from '@prisjakt/ui/list/variants';

import { addRecentSearch } from 'src/areas/search-suggestions/history-storage';
import { trackSearchCleared } from 'src/areas/search/tracking';

import {
  StyledSearchBar,
  InputWrapper,
  SearchInput,
  ClearIcon,
  ClearButton,
  SearchIcon,
  SearchButton,
  SearchButtonText,
  Text,
} from './styles';

import { AppState } from 'src/reducers';

type SearchBarProps = {
  query: string;
  setQuery: (query: string) => void;
  clearQuery: () => void;
  placeholderText?: string;
  autoFocus?: boolean;
  autoFocusOnExpand?: boolean;
  className?: string;
  onFocusChange?: () => void;
  selected?: number;
  onBlurChange?: () => void;
  onCloseSuggestions: () => void;
};

const SearchBar = (props: SearchBarProps) => {
  const {
    query,
    setQuery,
    clearQuery,
    className,
    autoFocusOnExpand,
    autoFocus = false,
    onFocusChange,
    selected,
    onBlurChange,
    onCloseSuggestions,
  } = props;

  const { t } = useContext(AppContext);
  const searchInputEl = useRef<HTMLInputElement>(null);
  const searchWrapperEl = useRef<HTMLDivElement>(null);
  const focused = useSelector((state: AppState) => state.areas.search.focused);

  const handleKeyInput = (e: KeyboardEvent) => {
    if (e.defaultPrevented) {
      return;
    }
    // Listen for Esc key with fallback for older browsers
    const key = e.key || e.keyCode;
    if (key === 'Escape' || key === 'Esc' || key === 27) {
      blurInput();
      onCloseSuggestions();
    }
  };

  useEffect(() => {
    document.addEventListener('keyup', handleKeyInput);
    return () => {
      document.removeEventListener('keyup', handleKeyInput);
    };
  }, []);

  const focusInput = (): void => {
    if (searchInputEl.current) {
      searchInputEl.current.focus();
    }
  };

  const blurInput = () => {
    if (searchInputEl.current) {
      searchInputEl.current.blur();
    }
  };

  const handleClearSearchClick = useCallback(
    (event: React.MouseEvent): void => {
      event.stopPropagation();
      clearQuery();
      focusInput();
      trackSearchCleared();
    },
    [clearQuery]
  );

  const handleSearchInputFocus = useCallback(() => {
    if (onFocusChange) {
      onFocusChange();
    }
  }, [onFocusChange]);

  const handleSearchInputBlur = useCallback(
    e => {
      if (searchWrapperEl.current?.contains(e.relatedTarget)) {
        // Dont blur input when clicking the clear button
        return;
      }

      if (onBlurChange) {
        onBlurChange();
      }
    },
    [onBlurChange]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setQuery(event.target.value),
    [setQuery]
  );

  const disabled = query.length < 2;

  const handleClick = () => {
    if (disabled) {
      return;
    }

    addRecentSearch(query);
    onCloseSuggestions();
  };

  return (
    <StyledSearchBar
      ref={searchWrapperEl}
      data-searchbar="true"
      className={className}
      $focused={!disabled}
    >
      <InputWrapper>
        <BodyMediumText>
          <SearchInput
            ref={searchInputEl}
            value={query}
            data-suggestions="search"
            autoFocus={autoFocus || autoFocusOnExpand}
            aria-expanded={focused}
            aria-owns="overlaySearchResults"
            aria-autocomplete="both"
            aria-activedescendant={selected ? `selection${selected}` : ''}
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            type="search"
            placeholder={t('search', 'search.placeholder')}
            onFocus={handleSearchInputFocus}
            onBlur={handleSearchInputBlur}
            onChange={handleChange}
          />
        </BodyMediumText>

        <ClearButton
          show={query.length > 0}
          data-testid="clear-search-button"
          onClick={handleClearSearchClick}
        >
          <ClearIcon size={12} />
        </ClearButton>
      </InputWrapper>

      <SearchButton
        disabled={disabled}
        onClick={handleClick}
        to={
          disabled ? undefined : `/search?search=${encodeURIComponent(query)}`
        }
      >
        <SearchButtonText>
          <Row gap={0.5}>
            <SearchIcon size={20} />
            <Text $visible={!disabled}>{t('header', 'search.button')}</Text>
          </Row>
        </SearchButtonText>
      </SearchButton>
    </StyledSearchBar>
  );
};

export default SearchBar;
